'use strict';
var Validator = function(config) {
    this.config = Odigeo._.extend({
        flightTypes: ['oneWayTrip', 'roundTrip', 'multipleTrip'],
        cabinClassTypes: ['BUSINESS', 'FIRST', 'TOURIST', 'PREMIUM_ECONOMY', 'ECONOMIC_DISCOUNTED', 'SHOW_ALL'],
    }, config);
    this.configMap = Odigeo._.keys(this.config);
};

var getDifferenceInDays = function(date1, date2) {
    var utils = Odigeo.Utils;
    var datesMap = Odigeo._.map([date1, date2], function(date) {
        return Odigeo._.isString(date) ? utils.parseDate(date, utils.formatDate.masks.isoDate) : date;
    });

    return Odigeo._.every(datesMap, Odigeo._.isDate) ? utils.getDifferenceInDays.apply(utils, datesMap) : Number.NaN;
};

module.exports = Validator;

Odigeo._.extend(Validator.prototype, {
    setConfig: function(options) {
        Odigeo._.extend(this.config, options);
    },
    resetConfig: function() {
        this.config = Odigeo._.pick(this.config, this.configMap);
    },
    validateSegmentsLength: function(segments) {
        var errors = [];
        if (!Odigeo._.isArray(segments)) {
            errors.push({
                key: 'searchSegments',
            });
        }
        return errors;
    },
    validateFlighType: function(type) {
        var errors = [];

        if (this.config.flightTypes.indexOf(type) < 0) {
            errors.push({
                key: 'flightType',
            });
        }
        return errors;
    },
    validateSegmentsLengthAndFlightType: function(segments, type) {
        var errors = [];
        var numSegments = segments.length;
        if ((type === 'oneWayTrip' && numSegments < 1) ||
            (type === 'roundTrip' && numSegments !== 2) ||
            (type === 'multipleTrip' && numSegments <= 1)) {
            errors.push({
                key: 'searchSegments',
            });
        }
        return errors;
    },
    validateSegmentsContent: function(segments, flightType, isAccommodationSearch) {
        var isRoundTrip = flightType === this.config.flightTypes[1];
        var isOneWay = flightType === this.config.flightTypes[0];

        return Odigeo._.reduce(segments, function(totalErrors, segment, idx) {
            var destinations = [segment.destination];
            var legErrors = [];
            var hasSameOriginDestination = false;
            var isNotFirst = idx > 0;

            // Skip validation of airports of 2nd leg (or more) if on RoundTrip or OneWay
            if (!((isRoundTrip || isOneWay) && isNotFirst) && !(isAccommodationSearch && isOneWay)) {
                // Check if the origin and the destination cities are the same
                legErrors = legErrors.concat(this.validateAirport(segment, idx));

                if (Odigeo._.isEmpty(legErrors)) {
                    hasSameOriginDestination = Odigeo._.some(destinations, function(destination) {
                        return segment.departure.geoNodeId === destination.geoNodeId &&
                            segment.departure.iata === destination.iata;
                    });
                }

                if (hasSameOriginDestination) {
                    legErrors.push({index: idx, key: 'destination', type: 'sameCity'});
                }

                this.trigger('sameCity', idx, hasSameOriginDestination, segment.departure);
            }

            return totalErrors.concat(legErrors);
        }, [], this);
    },
    validateAirport: function(segment, idx) {
        var legErrors = [];

        if (!this.isValidAirport(segment.departure)) {
            legErrors.push({index: idx, key: 'departure', type: 'invalid'});
        }
        if (!this.isValidAirport(segment.destination)) {
            legErrors.push({index: idx, key: 'destination', type: 'invalid'});
        }

        return legErrors;
    },
    isValidAirport: function(airport) {
        return !Odigeo._.isEmpty(airport) &&
            ((Odigeo._.has(airport, 'geoNodeId') && Odigeo._.isFinite(parseInt(airport.geoNodeId, 10))) ||
                (Odigeo._.has(airport, 'iata') && Odigeo._.isString(airport.iata) && airport.iata.length >= 3));
    },
    validateCabinClass: function(cabinClass) {
        var errors = [];
        if (this.config.cabinClassTypes.indexOf(cabinClass) < 0) {
            errors.push({
                key: 'cabinClass',
            });
        }
        return errors;
    },
    validateAccommodationDestination: function(accommodationDestination, isAccommodationSearch) {
        var errors = [];
        if (isAccommodationSearch && Odigeo._.isEmpty(accommodationDestination)) {
            errors.push({index: 1, key: 'destination', type: 'invalid'});
        }
        return errors;
    },
    validateCheckinDates: function(checkInDate, checkOutDate) {
        var errors = [];

        errors = errors.concat(this.validateHasCheckinDates(checkInDate, checkOutDate));

        if (errors.length === 0) {
            errors = errors.concat(this.validateCheckinDatesLength(checkInDate, checkOutDate));
        }

        return errors;
    },
    validateHasCheckinDates: function(checkInDate, checkOutDate) {
        var errors = [];
        var isCheckInDateEmpty = !this.isDate(checkInDate);
        var isCheckOutDateEmpty = !this.isDate(checkOutDate);

        if (!isCheckInDateEmpty || !isCheckOutDateEmpty) {
            if (isCheckInDateEmpty) {
                errors.push({
                    key: 'checkInDates',
                    index: 0,
                });
            }
            if (isCheckOutDateEmpty) {
                errors.push({
                    key: 'checkInDates',
                    index: 1,
                });
            }
        }

        return errors;
    },
    validateCheckinDatesLength: function(checkInDate, checkOutDate, key) {
        var errors = [];
        var accommodationDays = getDifferenceInDays(checkInDate, checkOutDate);

        if (!Odigeo._.isNaN(accommodationDays) && accommodationDays <= 0) {
            errors.push({
                key: key || 'checkInDates',
                index: 3,
                type: 'one-day-later',
            });
        } else if (!Odigeo._.isNaN(accommodationDays) && accommodationDays > 30) {
            errors.push({
                key: key || 'checkInDates',
                index: 3,
                type: 'max-thirty-days',
            });
        }

        return errors;
    },
    validateCalendarIncomplete: function(segments, type) {
        var errors = [];
        var isOneWay = type === this.config.flightTypes[0];
        var currentSegment = segments[0];

        if (Odigeo._.isUndefined(currentSegment) || !currentSegment.dateStr) {
            errors.push({
                index: 0,
                key: 'date',
                type: 'incomplete',
            });
        }

        if (isOneWay || segments.length === 1) {
            return errors;
        }

        var dates = segments.map(function(segment) {
            return (!Odigeo._.isUndefined(segment.dateStr) && !Odigeo._.isNull(segment.dateStr)) ? Date.parse(segment.dateStr) : null;
        });

        for (var i = 1; i < dates.length; i++) {
            var left = Odigeo._.max(Odigeo._.filter(dates.slice(0, i), Number));
            var right = dates[i];
            var isIncompleteDate = Odigeo._.isNull(right) || Odigeo._.isNaN(right);
            if (left > right || isIncompleteDate) {
                errors.push({
                    index: i,
                    key: 'date',
                    type: 'incomplete',
                });
            }
        }
        return errors;
    },
    validateCheckInCalendarIncomplete: function(checkInDate, checkOutDate) {
        var errors = [];
        var accommodationDays = getDifferenceInDays(checkInDate, checkOutDate);

        if (Odigeo._.isNaN(accommodationDays) || accommodationDays < 0) {
            errors.push({
                key: 'checkInDates',
                index: 3,
                type: 'incomplete',
            });
        }

        return errors;
    },
    validateCalendarOutdate: function(depDateStr) {
        var errors = [];
        var departureDate = new Date(depDateStr);
        var now = new Date();
        now.setHours(0, 0, 0, 0);
        if (this.isDate(depDateStr) && departureDate < now) {
            errors.push({
                index: 1,
                key: 'date',
                type: 'outdate',
            });
        }

        return errors;
    },
    validateTrainPassengers: function(numAdults, numChildren, numInfants, adultAges, childrenAges, infantAges) {
        var errors = [];
        // Check total passengers not greater than 9
        if (numAdults + numChildren + numInfants > 9) {
            errors.push({
                key: 'passengers',
                type: 'limits',
            });
        }
        // Check for no empty ages
        if (numAdults > adultAges.length || numChildren > childrenAges.length || numInfants > infantAges.length) {
            errors.push({
                key: 'passengers',
                type: 'agesRequired',
            });
        }

        // Check that infants (infant with ages 0-2) are not greater than adults
        if (numInfants > numAdults) {
            errors.push({
                key: 'passengers',
                type: 'infantsGreaterThanAdults',
            });
        }
        // Check that children (infant with ages >= 2) are not greater than adults * 2
        if (numChildren > numAdults * 2) {
            errors.push({
                key: 'passengers',
                type: 'infantsGreaterThanAdults',
            });
        }

        return errors;
    },
    validatePassengers: function(numAdults, numKids, numInfants) {
        var errors = [];

        if (numAdults + numKids + numInfants > 9 ||
            numInfants > numAdults) {
            errors.push({
                key: 'passengers',
                type: 'limits',
            });
        }

        return errors;
    },
    validateRoomChildrenAges: function(rooms) {
        return Odigeo._.reduce(rooms, function(errors, room, idx) {
            Odigeo._.find(room.childrenAges, function(value) {
                if (Odigeo._.isNull(value)) {
                    errors.push({
                        key: 'passengers',
                        index: idx,
                        type: 'error',
                    });
                    return true;
                }
                return false;
            });

            return errors;
        }, []);
    },
    isDate: function(date) {
        return Odigeo._.isDate(date) || !Odigeo._.isNaN(Date.parse(date));
    },
}, Backbone.Events);
